/**
 * Visitor-side cobrowse orchestrator. Tracks viewer count to drive the
 * presence chip, surfaces `control:request` through the host handler or the
 * default modal, gates cursor/click via `isControlGranted()`, and lets the
 * visitor hang up via the chip's End button.
 */

import { showConsentModal, type ConsentDecision } from '@/cobrowse/ConsentModal';
import { showPresenceChip, hidePresenceChip, type ChipState } from '@/cobrowse/PresenceChip';
import { createLogger } from '@/logger';

const log = createLogger('Cobrowse');

export type ControlRequestHandler = (info: {
  agentName: string;
  brandName: string;
}) => Promise<ConsentDecision>;

type SendUpstream = (msg: Record<string, unknown>) => void;

export interface CobrowseControllerOpts {
  /** Fires when the visitor clicks End on the chip — caller closes the live transport. */
  onVisitorEndLiveView: () => void;
}

export class CobrowseController {
  private viewerCount = 0;
  private controlGranted = false;
  private hostHandler: ControlRequestHandler | null = null;
  private send: SendUpstream | null = null;
  private chipState: ChipState | null = null;

  constructor(private readonly opts: CobrowseControllerOpts) {}

  /** Wired by Recorder once the live transport is open. */
  setSend(send: SendUpstream | null): void {
    this.send = send;
  }

  /** Host-page override for the default consent modal. Returns an unsubscribe. */
  setControlRequestHandler(handler: ControlRequestHandler | null): () => void {
    this.hostHandler = handler;
    return () => {
      if (this.hostHandler === handler) this.hostHandler = null;
    };
  }

  /**
   * The hard gate. `Recorder._handleCursorMessage` and `_handleClickMessage`
   * consult this before applying anything — while it returns false, agent
   * cursor moves and clicks must never reach the DOM.
   */
  isControlGranted(): boolean {
    return this.controlGranted;
  }

  /** Route an inbound server message; returns true if the controller consumed it. */
  handleServerMessage(msg: { type: string; [k: string]: unknown }): boolean {
    switch (msg.type) {
      case 'viewer:joined': {
        const count =
          typeof msg['viewerCount'] === 'number'
            ? (msg['viewerCount'] as number)
            : this.viewerCount + 1;
        this.viewerCount = count;
        this.refreshChip();
        return true;
      }
      case 'viewer:left': {
        const count =
          typeof msg['viewerCount'] === 'number'
            ? (msg['viewerCount'] as number)
            : Math.max(0, this.viewerCount - 1);
        this.viewerCount = count;
        // Last viewer leaving implicitly ends any control it held.
        if (count === 0) this.controlGranted = false;
        this.refreshChip();
        return true;
      }
      case 'control:request': {
        const agentName = typeof msg['agentName'] === 'string' ? (msg['agentName'] as string) : '';
        const brandName = typeof msg['brandName'] === 'string' ? (msg['brandName'] as string) : '';
        void this.handleControlRequest(agentName, brandName);
        return true;
      }
      case 'control:end': {
        this.controlGranted = false;
        this.refreshChip();
        return true;
      }
      case 'liveview:end': {
        // Agent hung up — server will close the viewer socket; we just
        // reflect that locally. onVisitorEndLiveView is only for the chip's
        // End button.
        this.controlGranted = false;
        this.viewerCount = 0;
        this.refreshChip();
        return true;
      }
      default:
        return false;
    }
  }

  reset(): void {
    this.viewerCount = 0;
    this.controlGranted = false;
    this.refreshChip();
  }

  private async handleControlRequest(agentName: string, brandName: string): Promise<void> {
    let decision: ConsentDecision;
    try {
      decision = this.hostHandler
        ? await this.hostHandler({ agentName, brandName })
        : await showConsentModal({ agentName, brandName });
    } catch (err) {
      log.warn('Consent handler threw; treating as denied', err as Error);
      decision = 'denied';
    }

    if (decision === 'granted') {
      this.controlGranted = true;
      this.refreshChip();
      this.send?.({ type: 'control:granted' });
    } else {
      this.send?.({ type: 'control:denied' });
    }
  }

  private refreshChip(): void {
    const nextState: ChipState | null =
      this.viewerCount === 0 ? null : this.controlGranted ? 'interacting' : 'viewing';

    if (nextState === null) {
      if (this.chipState !== null) hidePresenceChip();
      this.chipState = null;
      return;
    }
    if (this.chipState !== nextState) {
      this.chipState = nextState;
      showPresenceChip(nextState, () => this.onChipEndClicked());
    }
  }

  private onChipEndClicked(): void {
    this.send?.({ type: 'liveview:end', endedBy: 'visitor' });
    this.controlGranted = false;
    this.viewerCount = 0;
    this.refreshChip();
    this.opts.onVisitorEndLiveView();
  }
}
